import { useEffect } from 'react';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { isIosNative } from '../services/platform';

/**
 * Drive the bottom-panel dock position from the iOS soft keyboard, and scope the
 * webview resize mode to the panel session.
 *
 * The panel is pinned to `bottom: var(--kb-visible)`; this hook owns that CSS var,
 * the four `@capacitor/keyboard` listeners, and `setResizeMode`. While a panel is
 * open the webview must NOT auto-resize (`None`) — otherwise the panel and every
 * other input jump when the keyboard appears. On close we restore `Native` so all
 * other inputs (login / search / name / PAT) keep native keyboard avoidance.
 *
 * No-op on web and non-iOS native platforms.
 */
export default function useKeyboardDock({ active }) {
  useEffect(() => {
    if (!active || !isIosNative()) return;
    const root = document.documentElement;
    let handles = [];
    let cancelled = false;
    let keyboardShown = false;

    const setVisible = (px) => {
      if (!cancelled) root.style.setProperty('--kb-visible', `${px}px`);
    };
    const onShow = (info) => {
      keyboardShown = true;
      setVisible(info.keyboardHeight ?? 0);
    };
    const onHide = () => {
      keyboardShown = false;
      setVisible(0);
    };

    (async () => {
      try {
        await Keyboard.setScroll({ isDisabled: true });
        if (cancelled) return;
        await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        if (cancelled) return;
        handles = [
          await Keyboard.addListener('keyboardWillShow', onShow),
          await Keyboard.addListener('keyboardDidShow', onShow),
          await Keyboard.addListener('keyboardWillHide', onHide),
          await Keyboard.addListener('keyboardDidHide', onHide),
        ];
        if (cancelled) {
          handles.forEach((h) => { try { h && h.remove(); } catch { /* noop */ } });
          handles = [];
        }
      } catch {
        // Keyboard plugin unavailable (e.g. native project not synced) — the
        // panel still renders, pinned to the bottom of the viewport.
      }
    })();

    return () => {
      cancelled = true;
      root.style.removeProperty('--kb-visible');
      handles.forEach((h) => { try { h && h.remove(); } catch { /* noop */ } });

      // Restore native resize. If the keyboard was up, wait for it to finish
      // hiding (restoring mid-animation causes a resize jump); fall back to a
      // short timeout in case the hide event was already consumed.
      const restore = () =>
        Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
      if (!keyboardShown) {
        restore();
      } else {
        let restored = false;
        const once = () => {
          if (restored) return;
          restored = true;
          restore();
        };
        Keyboard.addListener('keyboardDidHide', once)
          .then((h) => setTimeout(() => { once(); try { h && h.remove(); } catch { /* noop */ } }, 800))
          .catch(restore);
      }
    };
  }, [active]);
}
